import React from 'react';

import Storage, { BE_PRISM_TOKEN } from 'lane-shared/helpers/Storage';
import { getTxToken, GetTxTokenReq, GetTxTokenResData } from './client/auth';

const TOKEN_EXPIRY_BUFFER = 5 * 60 * 1000;

export async function getToken(): Promise<string | undefined> {
  const item = await Storage.getItemMaybe(BE_PRISM_TOKEN);

  if (item) {
    const expiresPart = item.split('.')[1];

    if (expiresPart) {
      const info = JSON.parse(atob(expiresPart));
      const expires = new Date(info.exp * 1000);

      if (expires > new Date()) {
        return item;
      }
    }

    await Storage.removeItem(BE_PRISM_TOKEN);
  }

  return undefined;
}

export async function removeToken(): Promise<void> {
  await Storage.removeItem(BE_PRISM_TOKEN);
}

export async function storeToken(token: string): Promise<void> {
  await Storage.setItem(BE_PRISM_TOKEN, token);
}

export function subscribeToToken(callback: () => void): () => void {
  return Storage.subscribe(BE_PRISM_TOKEN, callback);
}

export function useToken({
  callback = () => {},
}: {
  callback?: () => void;
} = {}): {
  token: string | undefined;
  tokenLoading: boolean;
} {
  const [token, setToken] = React.useState<undefined | string>(undefined);
  const [tokenLoading, setTokenLoading] = React.useState<boolean>(true);

  React.useEffect(() => {
    (async () => {
      setToken(await getToken());
      setTokenLoading(false);
    })();

    const unsubFn = subscribeToToken(() => {
      getToken().then(newToken => {
        setToken(newToken);
        setTokenLoading(false);
        callback();
      });
    });

    return unsubFn;
  }, [callback]);

  return { token, tokenLoading };
}

export async function getValidTxToken(
  tokenRequest: GetTxTokenReq
): Promise<string> {
  try {
    const existingToken = await Storage.getItemMaybe(BE_PRISM_TOKEN);

    if (!existingToken) {
      return await fetchNewTxToken(tokenRequest);
    }

    const expiresAt = parseToken(existingToken);

    if (!expiresAt || !isTokenValid(expiresAt)) {
      return await fetchNewTxToken(tokenRequest);
    }

    if (isTokenExpiringSoon(expiresAt)) {
      fetchNewTxToken(tokenRequest);

      return existingToken;
    }

    return existingToken;
  } catch (error) {
    throw new Error(error.message || 'Failed to get valid TX token');
  }
}

async function fetchNewTxToken(tokenRequest: GetTxTokenReq): Promise<string> {
  const response = await getTxToken(tokenRequest);

  if (!response.ok) {
    throw new Error('Failed to fetch token from BuildingEnginesPrism API');
  }

  const { token } = (await response.data) as GetTxTokenResData;

  await storeToken(token);

  return token;
}

function parseToken(token: string): number | undefined {
  try {
    if (!isValidJWTFormat(token)) {
      return undefined;
    }

    const expiresPart = token.split('.')[1];
    const info = JSON.parse(atob(expiresPart));

    if (typeof info.exp !== 'number') {
      return undefined;
    }

    return info.exp * 1000;
  } catch {
    return undefined;
  }
}

function isValidJWTFormat(token: string): boolean {
  if (!token) {
    return false;
  }

  const parts = token.split('.');

  return parts.length === 3 && parts.every(part => part.length > 0);
}

function isTokenValid(expiresAt: number): boolean {
  return Date.now() < expiresAt;
}

function isTokenExpiringSoon(expiresAt: number): boolean {
  return Date.now() + TOKEN_EXPIRY_BUFFER > expiresAt;
}

/**
 * Resolves the appropriate token for API requests based on token management settings.
 *
 * @param params - Configuration for token resolution
 * @param params.enableTokenManagement - Whether token management is enabled (can be undefined)
 * @param params.isManualLogin - Whether the user is manually logged in (can be undefined)
 * @param params.getTokenForRequest - Function to retrieve the stored/session token
 * @returns Empty string if token management handles it, otherwise the retrieved token
 */

export async function resolveTokenForRequest(params: {
  enableTokenManagement: boolean | undefined;
  isManualLogin: boolean | undefined;
  getTokenForRequest: () => Promise<string | null | undefined>;
}): Promise<string> {
  const { enableTokenManagement, isManualLogin, getTokenForRequest } = params;

  if (!enableTokenManagement || isManualLogin) {
    const tokenResult = await getTokenForRequest();

    return tokenResult ?? '';
  }

  return '';
}
